import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PlayerName from "@/components/PlayerName";
import { Hourglass, UserPlus, Repeat, Users, X, Trash2, ChevronRight } from "lucide-react";

const divStr = (e) => [e.division, e.event_type, e.age_bracket].filter(Boolean).join(" · ");
const slugd = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const EV_ORDER = ["Men's Doubles", "Women's Doubles", "Mixed Doubles", "Coed Doubles", "Men's Singles", "Women's Singles",
  "Boys Doubles", "Girls Doubles", "Junior Mixed Doubles", "Boys Singles", "Girls Singles"];
const LVL_ORDER = ["Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Future Pro", "Pro", "All"];
const divRank = (k) => [
  EV_ORDER.findIndex((e) => k.includes(e)) + 1 || 99,
  k.includes("High Intermediate") ? 3 : LVL_ORDER.findIndex((l) => k.includes(l)) + 1 || 99,
];

function ReplacePicker({ tid, target, wlPeople, onDone, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (payload, ok) => {
    setBusy(true);
    try {
      await api.post(`/tournaments/${tid}/entries/${target.entryId}/set-player`, { slot: target.slot, ...payload });
      toast.success(ok);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const search = async (v) => {
    setQ(v);
    if (v.trim().length < 2) { setResults([]); return; }
    try { const { data } = await api.get(`/members/search?q=${encodeURIComponent(v.trim())}`); setResults(data); } catch { /* noop */ }
  };
  return (
    <div className="border border-pb-cyan/40 bg-black/40 p-3 mt-2 space-y-2.5" data-testid="player-picker">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-pb-cyan">
          {target.mode === "add" ? "Add a player to this team" : `Sub out ${target.name} — pick who comes in`}
        </span>
        <button data-testid="player-picker-close" onClick={onClose}><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
      </div>
      {wlPeople.length > 0 && (
        <div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">From the waiting list</div>
          <div className="flex flex-wrap gap-1.5">
            {wlPeople.map((p) => (
              <button key={`${p.wid}-${p.person}`} data-testid={`picker-wl-${p.wid}-${p.person}`} disabled={busy}
                onClick={() => run({ waitlist_id: p.wid, waitlist_person: p.person }, `${p.name} is in — pulled from the waiting list`)}
                className="border border-green-500/50 text-green-400 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 hover:bg-green-500/10 active:scale-95 transition-all">
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Search Bullpen members</div>
        <input data-testid="picker-member-search" value={q} onChange={(e) => search(e.target.value)} placeholder="Type a member's name…"
          className="w-full bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white focus:outline-none focus:border-pb-cyan" />
        {results.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {results.map((m) => (
              <button key={m.id} data-testid={`picker-member-${m.id}`} disabled={busy}
                onClick={() => run({ user_id: m.id }, `${m.name} is on the team`)}
                className="border border-pb-cyan/50 text-pb-cyan text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 hover:bg-pb-cyan/10 active:scale-95 transition-all">
                {m.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-1.5">
        <input data-testid="picker-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="…or type any name (non-member)"
          className="flex-1 min-w-0 bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white focus:outline-none" />
        <button data-testid="picker-apply-name" disabled={busy || !name.trim()}
          onClick={() => run({ name: name.trim() }, `${name.trim()} is on the team`)}
          className="border border-amber-500/60 text-amber-400 text-[10px] font-black uppercase tracking-widest px-3 hover:bg-amber-500/10 disabled:opacity-40 transition-all">
          Use Name
        </button>
      </div>
    </div>
  );
}

export default function TdPlayerManager({ tid, entries, waitlist, onChanged, levels, ageBrackets, eventTypes, eventConfig }) {
  const [busy, setBusy] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [picker, setPicker] = useState(null);
  const [openDivs, setOpenDivs] = useState({});
  const [da, setDa] = useState(null);
  const [daQ, setDaQ] = useState("");
  const [daRes, setDaRes] = useState([]);
  const divOpts = (levels || []).filter((l) => l !== "All");
  const evChoices = eventTypes || [];
  // per-event divisions/ages when the TD configured them, tournament-wide otherwise
  const cfgFor = (ev) => (eventConfig && ev && eventConfig[ev]) || null;
  const divsFor = (ev) => {
    const c = cfgFor(ev);
    if (c && !(c.levels || []).includes("All") && (c.levels || []).length) return c.levels;
    return divOpts.length ? divOpts : ["Beginner", "Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Future Pro", "Pro"];
  };
  const agesFor = (ev) => { const c = cfgFor(ev); return c ? (c.age_brackets || []) : (ageBrackets || []); };
  const divChoices = divsFor(da?.event_type || bulk?.event_type || evChoices[0]);
  const abChoices = agesFor(da?.event_type || bulk?.event_type || evChoices[0]);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const byUid = {};
  entries.forEach((e) => { if (e.user_id) byUid[e.user_id] = e; });
  const seen = new Set();
  const teams = [];
  for (const e of entries) {
    const mate = e.partner_id ? byUid[e.partner_id] : null;
    if (mate && mate.partner_id === e.user_id) {
      const key = [e.id, mate.id].sort().join("-");
      if (seen.has(key)) continue;
      seen.add(key);
      teams.push({ entryId: e.id, div: divStr(e), division: e.division, ageBracket: e.age_bracket, evLabel: e.event_type || "", players: [
        { slot: "player", user_id: e.user_id, name: e.player_name },
        { slot: "partner", user_id: mate.user_id, name: mate.player_name }] });
    } else {
      const players = [{ slot: "player", user_id: e.user_id, name: e.player_name }];
      if (e.partner_name) players.push({ slot: "partner", user_id: e.partner_id, name: e.partner_name });
      teams.push({ entryId: e.id, div: divStr(e), division: e.division, ageBracket: e.age_bracket, evLabel: e.event_type || "", players });
    }
  }
  const wlPeople = (waitlist || []).flatMap((wl) =>
    (wl.players || []).map((p) => ({ ...p, wid: wl.id, div: divStr(wl) })));
  const solos = teams.filter((tm) => tm.players.length === 1);
  const groupDivs = (list) => {
    const m = {};
    for (const tm of list) { const k = tm.div || "Unassigned"; (m[k] = m[k] || []).push(tm); }
    return Object.entries(m).sort(([a], [b]) => {
      const ra = divRank(a), rb = divRank(b);
      return ra[0] - rb[0] || ra[1] - rb[1] || a.localeCompare(b);
    });
  };
  const divGroups = groupDivs(teams);
  const unlinked = teams.flatMap((tm) => tm.players.filter((p) => !p.user_id).map((p) => p.name));

  const bulkLines = (bulk?.text || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [p, pt] = l.split(/\s*[\/,|]\s*/);
    return { player_name: p, partner_name: pt || null };
  });
  const submitBulk = () => act(() => api.post(`/tournaments/${tid}/entries/bulk-add`, {
    lines: bulkLines, event_type: bulk.event_type || null, division: bulk.division || null, age_bracket: bulk.age_bracket || null,
  }).then(({ data }) => {
    setBulk(null);
    if (data.skipped?.length) toast.message(`${data.skipped.length} already on the roster: ${data.skipped.join(", ")}`);
    const guests = data.added.filter((a) => !a.linked).length;
    if (guests) toast.message(`${guests} guest player${guests > 1 ? "s" : ""} added — they'll get linked when they join the site`);
  }), `${bulkLines.length} team(s) written to the roster`);

  return (
    <div className="border border-pb-cyan/40 bg-pb-cyan/5 p-4 mb-6" data-testid="td-player-manager">
      {teams.length === 0 && wlPeople.length === 0 && (
        <div className="text-xs text-zinc-400 mb-3" data-testid="roster-empty-hint">Roster is empty — build it manually below. Players don't need a Pickle Bullpen account to be placed; guests get linked automatically when they sign up with the same name.</div>
      )}
      <div className="mb-4 space-y-2">
        {!da && !bulk && (
          <div className="flex flex-wrap gap-2">
            <button data-testid="direct-add-open-btn" disabled={busy}
              onClick={() => setDa({ event_type: evChoices[0] || "", division: "", age_bracket: "", player: null, partner: null, guest: "", guestPartner: "" })}
              className="border border-lime-400/60 text-lime-400 hover:bg-lime-400 hover:text-black px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors">
              + Direct Add To Roster (overrides payment)
            </button>
            <button data-testid="bulk-add-open-btn" disabled={busy}
              onClick={() => setBulk({ event_type: evChoices[0] || "", division: "", age_bracket: "", text: "" })}
              className="border border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan hover:text-black px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors">
              ⌨ Build Roster Manually (paste names)
            </button>
          </div>
        )}
        {bulk && (
          <div className="border border-pb-cyan/40 bg-black/40 p-3 space-y-2" data-testid="bulk-add-panel">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-pb-cyan">Manual Roster — one team per line: <span className="text-zinc-400 normal-case tracking-normal">Player / Partner</span></span>
              <button data-testid="bulk-add-close" onClick={() => setBulk(null)}><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
            </div>
            <textarea data-testid="bulk-add-text" value={bulk.text} onChange={(e) => setBulk({ ...bulk, text: e.target.value })} rows={6}
              placeholder={"Jordan Fox / Sam Cho\nAce Alvarez / Bo Kim\nDee Lane"}
              className="w-full bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-pb-cyan" />
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {evChoices.length > 1 && (
                <select data-testid="bulk-add-event" value={bulk.event_type} onChange={(e) => setBulk((s) => ({ ...s, event_type: e.target.value, division: "", age_bracket: "" }))}
                  className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white">
                  {evChoices.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              )}
              <select data-testid="bulk-add-division" value={bulk.division} onChange={(e) => setBulk((s) => ({ ...s, division: e.target.value }))}
                className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white">
                <option value="">Division…</option>
                {divsFor(bulk.event_type).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              {agesFor(bulk.event_type).length > 0 && (
                <select data-testid="bulk-add-age" value={bulk.age_bracket} onChange={(e) => setBulk((s) => ({ ...s, age_bracket: e.target.value }))}
                  className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white">
                  <option value="">Age group…</option>
                  {agesFor(bulk.event_type).map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              )}
              <span className="text-zinc-500" data-testid="bulk-add-count">{bulkLines.length} team{bulkLines.length === 1 ? "" : "s"} · members matched by exact name, others join as guests</span>
              <button data-testid="bulk-add-submit" disabled={busy || bulkLines.length === 0 || !bulk.division} onClick={submitBulk}
                className="ml-auto bg-pb-cyan text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40">
                Write {bulkLines.length || ""} To Roster
              </button>
            </div>
          </div>
        )}
        {da && (
          <div className="border border-lime-400/40 bg-black/40 p-3 space-y-2" data-testid="direct-add-panel">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-lime-400">Direct Add — any player, no payment needed</span>
              <button data-testid="direct-add-close" onClick={() => { setDa(null); setDaQ(""); setDaRes([]); }}><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
            </div>
            <input data-testid="direct-add-search" value={daQ}
              onChange={async (e) => {
                const v = e.target.value; setDaQ(v);
                if (v.trim().length < 2) { setDaRes([]); return; }
                try { const { data } = await api.get(`/members/search?q=${encodeURIComponent(v.trim())}`); setDaRes(data); } catch {}
              }}
              placeholder={!da.player ? "Search any member for the PLAYER spot…" : "Search a member for the PARTNER spot (optional)…"}
              className="w-full bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white focus:outline-none focus:border-lime-400" />
            {daRes.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {daRes.map((m) => (
                  <button key={m.id} data-testid={`direct-add-pick-${m.id}`}
                    onClick={() => { setDa((s) => (!s.player ? { ...s, player: m } : { ...s, partner: m })); setDaQ(""); setDaRes([]); }}
                    className="border border-lime-400/50 text-lime-400 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 hover:bg-lime-400/10">
                    {m.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Not a member yet?</span>
              <input data-testid="direct-add-guest-name" value={da.guest || ""} onChange={(e) => setDa((s) => ({ ...s, guest: e.target.value, player: e.target.value ? null : s.player }))}
                placeholder="Type player name (guest)" className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white w-44 focus:outline-none focus:border-lime-400" />
              <input data-testid="direct-add-guest-partner" value={da.guestPartner || ""} onChange={(e) => setDa((s) => ({ ...s, guestPartner: e.target.value, partner: e.target.value ? null : s.partner }))}
                placeholder="Partner name (guest, optional)" className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white w-48 focus:outline-none focus:border-lime-400" />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-zinc-400">Player: <b className="text-white">{da.player?.name || da.guest || "—"}</b>{!da.player && da.guest && <span className="text-[9px] text-amber-400 ml-1">GUEST</span>}</span>
              <span className="text-zinc-400">Partner: <b className="text-white">{da.partner?.name || da.guestPartner || "none"}</b>
                {da.partner && <button className="ml-1 text-red-400" onClick={() => setDa((s) => ({ ...s, partner: null }))}>×</button>}</span>
              {evChoices.length > 1 && (
                <select data-testid="direct-add-event" value={da.event_type} onChange={(e) => setDa((s) => ({ ...s, event_type: e.target.value }))}
                  className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white">
                  {evChoices.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              )}
              <select data-testid="direct-add-division" value={da.division} onChange={(e) => setDa((s) => ({ ...s, division: e.target.value }))}
                className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white">
                <option value="">Division…</option>
                {divChoices.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              {abChoices.length > 0 && (
                <select data-testid="direct-add-age" value={da.age_bracket} onChange={(e) => setDa((s) => ({ ...s, age_bracket: e.target.value }))}
                  className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white">
                  <option value="">Age bracket…</option>
                  {abChoices.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              )}
              <button data-testid="direct-add-submit" disabled={busy || (!da.player && !(da.guest || "").trim())}
                onClick={() => act(() => api.post(`/tournaments/${tid}/entries/direct-add`, {
                  user_id: da.player?.id || null, player_name: da.player ? null : (da.guest || "").trim(),
                  partner_user_id: da.partner?.id || null, partner_name: da.partner ? null : ((da.guestPartner || "").trim() || null),
                  event_type: da.event_type || null, division: da.division || null, age_bracket: da.age_bracket || null,
                }).then(() => { setDa(null); setDaQ(""); setDaRes([]); }),
                  `${da.player?.name || da.guest}${(da.partner?.name || da.guestPartner) ? " / " + (da.partner?.name || da.guestPartner) : ""} added straight onto the roster`)}
                className="ml-auto bg-lime-400 text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-lime-300 disabled:opacity-40">
                Add To Roster
              </button>
            </div>
          </div>
        )}
      </div>
      {wlPeople.length > 0 && (
        <>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-cyan font-bold mb-1.5">
            <Hourglass className="w-3.5 h-3.5" /> Waiting List — Individual Players ({wlPeople.length})
          </div>
          <p className="text-[11px] text-zinc-500 mb-2">
            Everyone waits as an individual — promote them into the tournament on their own, or sub them straight onto any team below.
          </p>
          <div className="space-y-1.5 mb-4">
            {wlPeople.map((p, i) => (
              <div key={`${p.wid}-${p.person}`} className="flex flex-wrap items-center gap-2 border border-pb-border/60 px-3 py-2 text-sm"
                data-testid={`wl-person-${p.wid}-${p.person}`}>
                <span className="text-zinc-600 font-mono text-xs">#{i + 1}</span>
                <PlayerName userId={p.user_id} name={p.name} />
                <span className="text-[10px] text-zinc-500">{p.div}</span>
                <span className="ml-auto flex items-center gap-2">
                  <button data-testid={`wl-promote-${p.wid}-${p.person}`} disabled={busy}
                    onClick={() => act(() => api.post(`/tournaments/${tid}/waitlist/${p.wid}/promote`, { person: p.person }),
                      `${p.name} promoted into the tournament`)}
                    className="flex items-center gap-1 border border-green-500/60 text-green-400 font-bold uppercase tracking-widest text-[9px] px-2.5 py-1.5 hover:bg-green-500/10 active:scale-95 transition-all">
                    <UserPlus className="w-3 h-3" /> Promote
                  </button>
                  <button data-testid={`wl-remove-${p.wid}-${p.person}`} disabled={busy} title="Remove from waiting list"
                    onClick={() => act(() => api.delete(`/tournaments/${tid}/waitlist/${p.wid}?person=${p.person}`))}
                    className="text-zinc-500 hover:text-pb-red font-bold px-1.5">✕</button>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      {unlinked.length > 0 && (
        <div className="border border-red-500/50 bg-red-500/10 px-3 py-2 mb-3" data-testid="roster-link-warning">
          <div className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-0.5">
            ⚠ {unlinked.length} roster spot{unlinked.length > 1 ? "s aren't" : " isn't"} linked to a real account
          </div>
          <div className="text-[11px] text-zinc-400">
            {unlinked.join(", ")} — they won't see court assignments, scores or anything in their Players Hub.
            Tap ⇄ on their name and pick them from member search to link their account.
          </div>
        </div>
      )}
      {teams.length > 0 && (
        <>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold mb-1.5">
            <Users className="w-3.5 h-3.5" /> Team Rosters — Individual Substitutions
          </div>
          <p className="text-[11px] text-zinc-500 mb-2">
            Manage people, not teams: tap ⇄ on any player to sub them out (waitlist, member search, or any name), ✕ to drop a partner, or Add Player to complete a team. Use the event, division and age dropdowns to move an entry that signed up in the wrong bracket, or the trash icon to delete a whole team. Brackets keep the team's spot — only the person changes.
          </p>
          {solos.length > 0 && (
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-pb-cyan mb-2" data-testid="roster-solo-note">
              {solos.length} solo player{solos.length > 1 ? "s" : ""} still need{solos.length > 1 ? "" : "s"} a partner — they're listed first inside their division below.
            </div>
          )}
          <div className="space-y-2" data-testid="roster-accordions">
            {divGroups.map(([dv, rows]) => {
              const open = openDivs[dv] ?? divGroups.length === 1;
              const soloCount = rows.filter((tm) => tm.players.length === 1).length;
              const sorted = [...rows].sort((a, b) => a.players.length - b.players.length);
              return (
                <div key={dv} className="border border-pb-border/60" data-testid={`roster-accordion-${slugd(dv)}`}>
                  <button type="button" data-testid={`roster-accordion-toggle-${slugd(dv)}`}
                    onClick={() => setOpenDivs((p) => ({ ...p, [dv]: !open }))}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${open ? "bg-pb-elevated/60" : "hover:bg-pb-elevated/40"}`}>
                    <ChevronRight className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open ? "rotate-90" : ""}`} />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-amber-300 flex-1">{dv}</span>
                    {soloCount > 0 && (
                      <span className="text-[9px] font-bold uppercase tracking-widest text-pb-cyan border border-pb-cyan/40 px-1.5 py-0.5">
                        {soloCount} need{soloCount > 1 ? "" : "s"} partner
                      </span>
                    )}
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      {rows.length} {rows.length === 1 ? "entry" : "entries"}
                    </span>
                  </button>
                  {open && (
                    <div className="space-y-1.5 p-2" data-testid={`roster-accordion-body-${slugd(dv)}`}>
                      {sorted.map((tm) => (
              <div key={tm.entryId} className="border border-pb-border/60 px-3 py-2" data-testid={`team-row-${tm.entryId}`}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {tm.players.map((p) => (
                    <span key={p.slot} className="flex items-center gap-1 border border-pb-border bg-pb-elevated/60 pl-2.5 pr-1 py-1"
                      data-testid={`team-${tm.entryId}-${p.slot}`}>
                      <PlayerName userId={p.user_id} name={p.name} />
                      {!p.user_id && <span title="Not linked to a real account — this player sees nothing in their Players Hub. Sub them via member search to link." className="text-[8px] font-black uppercase tracking-widest text-red-400 border border-red-500/50 px-1 py-0.5" data-testid={`unlinked-${tm.entryId}-${p.slot}`}>no account</span>}
                      <button title={`Sub ${p.name} out for another player`} data-testid={`swap-${tm.entryId}-${p.slot}`} disabled={busy}
                        onClick={() => setPicker({ entryId: tm.entryId, slot: p.slot, name: p.name, mode: "swap" })}
                        className="text-amber-400 hover:bg-amber-500/15 p-1 transition-colors"><Repeat className="w-3.5 h-3.5" /></button>
                      {p.slot === "partner" && (
                        <button title="Remove from team" data-testid={`remove-${tm.entryId}-partner`} disabled={busy}
                          onClick={() => act(() => api.post(`/tournaments/${tid}/entries/${tm.entryId}/set-player`, { slot: "partner", remove: true }),
                            `${p.name} removed from the team`)}
                          className="text-zinc-500 hover:text-pb-red p-1 transition-colors"><X className="w-3.5 h-3.5" /></button>
                      )}
                    </span>
                  ))}
                  {tm.players.length === 1 && (
                    <button data-testid={`add-player-${tm.entryId}`} disabled={busy}
                      onClick={() => setPicker({ entryId: tm.entryId, slot: "partner", mode: "add" })}
                      className="flex items-center gap-1 border border-dashed border-green-500/50 text-green-400 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 hover:bg-green-500/10 active:scale-95 transition-all">
                      <UserPlus className="w-3 h-3" /> Add Player
                    </button>
                  )}
                  <span className="ml-auto flex items-center gap-1.5">
                    {evChoices.length > 1 ? (
                      <select data-testid={`event-select-${tm.entryId}`} disabled={busy}
                        value={tm.evLabel || ""} title="Move this entry to a different event"
                        onChange={(ev) => act(() => api.put(`/tournaments/${tid}/entries/${tm.entryId}/event-type`, { event_type: ev.target.value }),
                          `Moved to ${ev.target.value}`)}
                        className="bg-pb-elevated border border-pb-border text-[10px] uppercase tracking-wider text-zinc-300 px-1.5 py-1 focus:outline-none focus:border-zinc-400 cursor-pointer">
                        {!tm.evLabel && <option value="">Event…</option>}
                        {tm.evLabel && !evChoices.includes(tm.evLabel) && <option value={tm.evLabel}>{tm.evLabel} (old)</option>}
                        {evChoices.map((e) => <option key={e} value={e}>{e}</option>)}
                      </select>
                    ) : (
                      tm.evLabel && <span className="text-[10px] text-zinc-500">{tm.evLabel}</span>
                    )}
                    <select data-testid={`division-select-${tm.entryId}`} disabled={busy}
                      value={tm.division || ""} title="Move this entry to a different division"
                      onChange={(ev) => act(() => api.put(`/tournaments/${tid}/entries/${tm.entryId}/division`, { division: ev.target.value }),
                        `Moved to ${ev.target.value}`)}
                      className="bg-pb-elevated border border-pb-border text-[10px] uppercase tracking-wider text-amber-300 px-1.5 py-1 focus:outline-none focus:border-amber-400 cursor-pointer">
                      {!tm.division && <option value="">Division…</option>}
                      {divChoices.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    {abChoices.length > 0 && (
                      <select data-testid={`age-bracket-select-${tm.entryId}`} disabled={busy}
                        value={tm.ageBracket || ""} title="Move this entry to a different age bracket"
                        onChange={(ev) => act(() => api.put(`/tournaments/${tid}/entries/${tm.entryId}/age-bracket`, { age_bracket: ev.target.value }),
                          `Moved to ${ev.target.value}`)}
                        className="bg-pb-elevated border border-pb-border text-[10px] uppercase tracking-wider text-pb-cyan px-1.5 py-1 focus:outline-none focus:border-pb-cyan cursor-pointer">
                        {!tm.ageBracket && <option value="">Age…</option>}
                        {tm.ageBracket && !abChoices.includes(tm.ageBracket) && <option value={tm.ageBracket}>{tm.ageBracket} (old)</option>}
                        {abChoices.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    )}
                    <button title="Delete this entire team from the tournament" data-testid={`delete-team-${tm.entryId}`} disabled={busy}
                      onClick={() => {
                        const names = tm.players.map((p) => p.name).join(" / ");
                        if (window.confirm(`Delete ${names} from the tournament? This removes the whole team entry and can't be undone.`)) {
                          act(() => api.delete(`/tournaments/${tid}/entries/${tm.entryId}`), `${names} removed from the tournament`);
                        }
                      }}
                      className="text-zinc-500 hover:text-pb-red hover:bg-pb-red/10 p-1.5 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                </div>
                {picker && picker.entryId === tm.entryId && (
                  <ReplacePicker tid={tid} target={picker} wlPeople={wlPeople}
                    onDone={() => { setPicker(null); onChanged(); }} onClose={() => setPicker(null)} />
                )}
              </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
